import { supabase } from "@/integrations/supabase/client";

interface StreamLimitResult {
  hasStreams: boolean;
  streamsRemaining: number;
  error?: string;
}

/**
 * Retry helper with exponential backoff
 */
async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = 2,
  baseDelay: number = 300,
): Promise<{ data: T | null; error: string | null }> {
  let lastError: string | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await fn();
      return { data: result, error: null };
    } catch (error) {
      lastError = error instanceof Error ? error.message : "Unknown error";
      console.warn(
        `⚠️ Retry attempt ${attempt + 1}/${maxRetries + 1} failed:`,
        lastError,
      );

      if (attempt < maxRetries) {
        await new Promise((r) => setTimeout(r, baseDelay * (attempt + 1)));
      }
    }
  }

  return { data: null, error: lastError };
}

/**
 * Check if a fan user has remaining streams for today
 * Uses retry logic and fails open on persistent errors
 */
export async function checkFanStreamLimit(
  userId: string,
): Promise<StreamLimitResult> {
  try {
    // First check if user has remaining streams with retry
    const checkResult = await withRetry(async () => {
      const { data, error } = await supabase.rpc("has_remaining_streams", {
        p_user_id: userId,
      });

      if (error) throw error;
      return data;
    });

    if (checkResult.error) {
      console.error(
        "❌ Error checking stream limit after retries:",
        checkResult.error,
      );
      // FAIL OPEN: Allow playback on persistent errors
      return {
        hasStreams: true,
        streamsRemaining: 50,
        error: "Failed to check stream limit",
      };
    }

    const hasStreams = checkResult.data === true;

    // If definitely no streams, return early
    if (!hasStreams) {
      return { hasStreams: false, streamsRemaining: 0 };
    }

    // Ensure daily stream limit exists (don't block on this)
    try {
      await supabase.rpc("get_or_create_daily_stream_limit", {
        p_user_id: userId,
      });
    } catch (e) {
      console.warn("⚠️ Failed to ensure daily stream limit exists:", e);
    }

    // Get detailed stream limit info with retry
    const limitResult = await withRetry(async () => {
      const { data, error } = await supabase
        .from("daily_stream_limits")
        .select("*")
        .eq("user_id", userId)
        .eq("date", new Date().toISOString().split("T")[0])
        .maybeSingle();

      if (error) throw error;
      return data;
    });

    if (limitResult.error) {
      console.error(
        "❌ Error getting stream limit details after retries:",
        limitResult.error,
      );
      // FAIL OPEN: Allow playback, assume they have streams since has_remaining_streams returned true
      return {
        hasStreams: true,
        streamsRemaining: 50,
        error: "Failed to get stream details",
      };
    }

    const streamsRemaining = limitResult.data?.streams_remaining ?? 50;

    return {
      hasStreams: true,
      streamsRemaining,
    };
  } catch (error) {
    console.error("🔥 Exception in checkFanStreamLimit:", error);
    // FAIL OPEN: Allow playback on unexpected errors
    return {
      hasStreams: true,
      streamsRemaining: 50,
      error: "Network error checking streams",
    };
  }
}

/**
 * Consume one stream for a fan user with retry logic
 */
export async function consumeFanStream(userId: string): Promise<boolean> {
  const result = await withRetry(async () => {
    const { data, error } = await supabase.rpc("consume_stream", {
      p_user_id: userId,
    });

    if (error) throw error;
    return data === true;
  });

  if (result.error) {
    console.error("❌ Error consuming fan stream after retries:", result.error);
    // FAIL OPEN: Return true to not block playback on network issues
    // The fraud prevention system will catch abuse
    return true;
  }

  return result.data === true;
}
